import { useEffect, useMemo, useState } from "react"
import { Copy, ExternalLink, Loader2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Alert,
  AlertDescription,
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"
import { Agent, ClientChannelType } from "@/types"
import { useSysConfig, useToast } from "@/hooks"
import { agentResponseToAgent, useGetPublishedAgentsApi } from "@/hooks/api"
import { isMaskedValue } from "../utils"

type MetaService = ClientChannelType.MESSENGER | ClientChannelType.INSTAGRAM
const DEFAULT_CONVERSATION_SESSION_TIMEOUT_HOURS = 24

export interface MetaConnectionData {
  resourceId: string
  webhookVerifyToken: string
  metaAppSecret: string
  accessToken: string
  agentId: string
  conversationSessionTimeoutHours: number
}

export interface MetaConnectionFormProps {
  service: MetaService
  channelId: string
  workspaceId: string
  organizationId: string
  initialData?: Partial<MetaConnectionData>
  onSubmit: (data: MetaConnectionData) => void
  onCancel?: () => void
  isLoading?: boolean
  onDirtyChange?: (isDirty: boolean) => void
}

const buildApiUrl = (serverRoot: string, path: string) => {
  if (!serverRoot) {
    return path
  }
  return new URL(path, serverRoot).toString()
}

const createMetaFormSchema = (
  t: (key: string) => string,
  service: MetaService
) => {
  const baseKey = `workspace.channels.${service}.form.errors`

  return z.object({
    resourceId: z.string().min(1, {
      message: t(`${baseKey}.resourceIdRequired`),
    }),
    webhookVerifyToken: z.string().min(1, {
      message: t(`${baseKey}.webhookVerifyTokenRequired`),
    }),
    metaAppSecret: z.string().min(1, {
      message: t(`${baseKey}.metaAppSecretRequired`),
    }),
    accessToken: z.string().min(1, {
      message: t(`${baseKey}.accessTokenRequired`),
    }),
    agentId: z.string().min(1, {
      message: t(`${baseKey}.agentRequired`),
    }),
    conversationSessionTimeoutHours: z.coerce
      .number()
      .int({
        message: t(`${baseKey}.conversationSessionTimeoutHoursInteger`),
      })
      .min(1, {
        message: t(`${baseKey}.conversationSessionTimeoutHoursRange`),
      })
      .max(720, {
        message: t(`${baseKey}.conversationSessionTimeoutHoursRange`),
      }),
  })
}

type MetaFormData = z.infer<ReturnType<typeof createMetaFormSchema>>

const getMetaFormDefaults = (
  initialData?: Partial<MetaConnectionData>
): MetaFormData => ({
  resourceId: initialData?.resourceId || "",
  webhookVerifyToken: initialData?.webhookVerifyToken || "",
  metaAppSecret: initialData?.metaAppSecret || "",
  accessToken: initialData?.accessToken || "",
  agentId: initialData?.agentId || "",
  conversationSessionTimeoutHours:
    initialData?.conversationSessionTimeoutHours ??
    DEFAULT_CONVERSATION_SESSION_TIMEOUT_HOURS,
})

const META_SETUP_URLS: Record<MetaService, string> = {
  [ClientChannelType.MESSENGER]: "https://developers.facebook.com/apps/",
  [ClientChannelType.INSTAGRAM]: "https://developers.facebook.com/apps/",
}

export const MetaConnectionForm = ({
  service,
  channelId,
  workspaceId,
  organizationId,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  onDirtyChange,
}: MetaConnectionFormProps) => {
  const { t } = useTranslation()
  const { successToast } = useToast()
  const sysConfig = useSysConfig()
  const [agents, setAgents] = useState<Agent[]>([])
  const translationBase = `workspace.channels.${service}`
  const initialResourceId = initialData?.resourceId || ""
  const initialWebhookVerifyToken = initialData?.webhookVerifyToken || ""
  const initialMetaAppSecret = initialData?.metaAppSecret || ""
  const initialAccessToken = initialData?.accessToken || ""
  const initialAgentId = initialData?.agentId || ""
  const formDefaults = useMemo(
    () =>
      getMetaFormDefaults({
        resourceId: initialResourceId,
        webhookVerifyToken: initialWebhookVerifyToken,
        metaAppSecret: initialMetaAppSecret,
        accessToken: initialAccessToken,
        agentId: initialAgentId,
        conversationSessionTimeoutHours:
          initialData?.conversationSessionTimeoutHours,
      }),
    [
      initialAccessToken,
      initialAgentId,
      initialMetaAppSecret,
      initialResourceId,
      initialWebhookVerifyToken,
      initialData?.conversationSessionTimeoutHours,
    ]
  )

  const MetaFormSchema = createMetaFormSchema(t, service)
  const form = useForm<MetaFormData>({
    resolver: zodResolver(MetaFormSchema),
    defaultValues: formDefaults,
  })
  const { isDirty } = form.formState

  const webhookUrl = useMemo(
    () =>
      channelId
        ? buildApiUrl(
            sysConfig.serverRoot,
            `/api/v1/meta/events/${service}/${channelId}`
          )
        : "",
    [channelId, service, sysConfig.serverRoot]
  )

  const { data: agentsRespData, fetchFn: getAgents } =
    useGetPublishedAgentsApi()

  useEffect(() => {
    if (!workspaceId || !organizationId) return
    getAgents({
      searchParams: new URLSearchParams({
        wid: workspaceId,
        oid: organizationId,
      }),
    })
  }, [getAgents, organizationId, workspaceId])

  useEffect(() => {
    if (agentsRespData?.data) {
      setAgents(agentsRespData.data.map(agentResponseToAgent))
    }
  }, [agentsRespData])

  useEffect(() => {
    form.reset(formDefaults)
  }, [channelId, form, formDefaults])

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    successToast({
      title: t("common.copied"),
      description: t("common.copiedToClipboard"),
    })
  }

  const handleSubmit = (data: MetaFormData) => {
    onSubmit(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Alert>
          <AlertDescription>
            <div className="space-y-3">
              <p className="font-medium">
                {t(`${translationBase}.setup.title`)}
              </p>
              <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                {t(`${translationBase}.setup.instructions`)}
              </pre>
              <a
                href={META_SETUP_URLS[service]}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {t(`${translationBase}.setup.openConsole`)}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </AlertDescription>
        </Alert>

        {webhookUrl && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t(`${translationBase}.form.webhookUrl`)}
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={webhookUrl}
                readOnly
                className="font-mono text-xs bg-muted"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(webhookUrl)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t(`${translationBase}.form.webhookUrlDescription`)}
            </p>
          </div>
        )}

        <FormField
          control={form.control}
          name="resourceId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(`${translationBase}.form.resourceId`)}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t(
                    `${translationBase}.form.resourceIdPlaceholder`
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t(`${translationBase}.form.resourceIdDescription`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="webhookVerifyToken"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(`${translationBase}.form.webhookVerifyToken`)}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={t(
                    `${translationBase}.form.webhookVerifyTokenPlaceholder`
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t(`${translationBase}.form.webhookVerifyTokenDescription`)}
                {isMaskedValue(initialData?.webhookVerifyToken) && (
                  <span className="block">
                    {t(`${translationBase}.form.maskedValueNotice`)}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="metaAppSecret"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(`${translationBase}.form.metaAppSecret`)}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={t(
                    `${translationBase}.form.metaAppSecretPlaceholder`
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t(`${translationBase}.form.metaAppSecretDescription`)}
                {isMaskedValue(initialData?.metaAppSecret) && (
                  <span className="block">
                    {t(`${translationBase}.form.maskedValueNotice`)}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="accessToken"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(`${translationBase}.form.accessToken`)}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder={t(
                    `${translationBase}.form.accessTokenPlaceholder`
                  )}
                  className="font-mono"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                {t(`${translationBase}.form.accessTokenDescription`)}
                {isMaskedValue(initialData?.accessToken) && (
                  <span className="block">
                    {t(`${translationBase}.form.maskedValueNotice`)}
                  </span>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="agentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(`${translationBase}.form.agent`)}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        `${translationBase}.form.agentPlaceholder`
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {t(`${translationBase}.form.agentDescription`)}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="conversationSessionTimeoutHours"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(`${translationBase}.form.conversationSessionTimeoutHours`)}{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="number" min={1} max={720} step={1} {...field} />
              </FormControl>
              <FormDescription>
                {t(
                  `${translationBase}.form.conversationSessionTimeoutHoursDescription`
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default MetaConnectionForm
